import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ComparisonTable.css";

const iconMap = {
  check: "/svgs/checkmark-badge-01.svg",
  cross: "/svgs/cancel-circle.svg",
};

const ComparisonTable = ({
  onEnrollClick,
  translationPrefix = "scholarships",
}) => {
  const { t, i18n } = useTranslation();
  const tableRef = useRef(null);
  const containerRef = useRef(null);
  const bammbuuHeaderRef = useRef(null);
  const [highlightHeight, setHighlightHeight] = useState(0);
  const [highlightLeft, setHighlightLeft] = useState(340);
  const [highlightWidth, setHighlightWidth] = useState(240);

  const features = [
    {
      label: t(`${translationPrefix}.comparison.features.targetedExamPrep`),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.specialized`),
      },
      apps: t(`${translationPrefix}.comparison.apps.limitedBasic`),
      tutors: t(`${translationPrefix}.comparison.tutors.tutorDependent`),
      generic: t(`${translationPrefix}.comparison.generic.variable`),
    },
    {
      label: t(
        `${translationPrefix}.comparison.features.certifiedExpertInstructors`,
      ),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.vettedProfessionals`),
      },
      apps: t(`${translationPrefix}.comparison.apps.notApplicable`),
      tutors: t(`${translationPrefix}.comparison.tutors.variableQuality`),
      generic: t(`${translationPrefix}.comparison.generic.oftenPrerecorded`),
    },
    {
      label: t(
        `${translationPrefix}.comparison.features.customizedLearningPlan`,
      ),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.tailoredByExperts`),
      },
      apps: t(`${translationPrefix}.comparison.apps.basicAlgorithm`),
      tutors:
        translationPrefix === "interviewAccelerator"
          ? {
              icon: "cross",
              text: t(`${translationPrefix}.comparison.tutors.possibleExtraCost`),
            }
          : t(`${translationPrefix}.comparison.tutors.possibleExtraCost`),
      generic: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.generic.no`),
      },
    },
    {
      label:
        t(`${translationPrefix}.comparison.features.fluencyPlaybook`) !==
        `${translationPrefix}.comparison.features.fluencyPlaybook`
          ? t(`${translationPrefix}.comparison.features.fluencyPlaybook`)
          : t(`${translationPrefix}.comparison.features.liveClassesIncluded`),
      bammbuu: {
        icon: "check",
        text:
          t(`${translationPrefix}.comparison.bammbuu.includedFree`) !==
          `${translationPrefix}.comparison.bammbuu.includedFree`
            ? t(`${translationPrefix}.comparison.bammbuu.includedFree`)
            : t(`${translationPrefix}.comparison.bammbuu.tenPerMonth`),
      },
      apps: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.apps.no`),
      },
      tutors: t(`${translationPrefix}.comparison.tutors.payPerClass`),
      generic: t(`${translationPrefix}.comparison.generic.rareAddon`),
    },
    {
      label: t(
        `${translationPrefix}.comparison.features.liveConversationPractice`,
      ),
      bammbuu: {
        icon: "check",
        text: (() => {
          const fullText = t(
            `${translationPrefix}.comparison.bammbuu.unlimitedConversationClasses`,
          );
          const isSpanish = i18n.language.startsWith("es");
          const words = fullText.split(" ");

          if (isSpanish) {
            // For Spanish, bold the last word "Ilimitadas"
            const lastWord = words[words.length - 1];
            const rest = words.slice(0, -1).join(" ");
            return (
              <span>
                {rest} <span className="font-bold italic">{lastWord}</span>
              </span>
            );
          } else {
            // For English, bold the first word "Unlimited"
            const firstWord = words[0];
            const rest = words.slice(1).join(" ");
            return (
              <span>
                <span className="font-bold italic">{firstWord}</span> {rest}
              </span>
            );
          }
        })(),
      },
      apps: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.apps.no`),
      },
      tutors: t(`${translationPrefix}.comparison.tutors.payPerClassNotFocused`),
      generic: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.generic.no`),
      },
    },
    {
      label: t(`${translationPrefix}.comparison.features.aiPracticeTutor`),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.onDemand`),
      },
      apps: t(`${translationPrefix}.comparison.apps.limitedAIExercises`),
      tutors: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.generic.no`),
      },
      generic: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.generic.no`),
      },
    },
    {
      label: t(
        `${translationPrefix}.comparison.features.personalizedResources`,
      ),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.curatedForYou`),
      },
      apps: t(`${translationPrefix}.comparison.apps.standardLibrary`),
      tutors: t(`${translationPrefix}.comparison.tutors.variableQuality`),
      generic: t(`${translationPrefix}.comparison.generic.generic`),
    },
    {
      label: t(`${translationPrefix}.comparison.features.directSupport`),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.bammbuuTeam`),
      },
      apps: t(`${translationPrefix}.comparison.apps.appSupport`),
      tutors: t(`${translationPrefix}.comparison.tutors.platformOnly`),
      generic: t(`${translationPrefix}.comparison.generic.limited`),
    },
    {
      label:
        t(`${translationPrefix}.comparison.features.conversationBonuses`) !==
        `${translationPrefix}.comparison.features.conversationBonuses`
          ? t(`${translationPrefix}.comparison.features.conversationBonuses`)
          : t(`${translationPrefix}.comparison.features.moneyBackGuarantee`),
      bammbuu: {
        icon: "check",
        text: t(`${translationPrefix}.comparison.bammbuu.resultsFocused`),
      },
      apps: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.apps.no`),
      },
      tutors: {
        icon: "cross",
        text: t(`${translationPrefix}.comparison.apps.no`),
      },
      generic: t(`${translationPrefix}.comparison.generic.rarely`),
    },
    {
      label: t(`${translationPrefix}.comparison.features.studentValue`),
      bammbuu: t(`${translationPrefix}.comparison.bammbuu.allInclusiveValue`),
      apps: t(`${translationPrefix}.comparison.apps.lowBasicFeatures`),
      tutors: t(`${translationPrefix}.comparison.tutors.limitedVariableValue`),
      generic: t(
        `${translationPrefix}.comparison.generic.limitedBasicFeatures`,
      ),
    },
  ];

  useEffect(() => {
    function updateHighlight() {
      if (
        tableRef.current &&
        bammbuuHeaderRef.current &&
        containerRef.current
      ) {
        setHighlightHeight(tableRef.current.offsetHeight);

        // Calculate the actual position of the Bammbuu column relative to the container
        const containerRect = containerRef.current.getBoundingClientRect();
        const bammbuuRect = bammbuuHeaderRef.current.getBoundingClientRect();

        // Position relative to the container (getBoundingClientRect is viewport-relative, so we subtract)
        const left = bammbuuRect.left - containerRect.left;
        const width = bammbuuRect.width;

        setHighlightLeft(left);
        setHighlightWidth(width);
      }
    }

    // Initial calculation (with a small delay to ensure layout is complete)
    const timeoutId = setTimeout(updateHighlight, 0);

    // Update on resize
    window.addEventListener("resize", updateHighlight);

    // Update on scroll (in case the container scrolls horizontally)
    const scrollHandler = () => {
      requestAnimationFrame(updateHighlight);
    };
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", scrollHandler);
    }

    // Also update when table content might change
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(updateHighlight);
    });
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }
    if (bammbuuHeaderRef.current) {
      resizeObserver.observe(bammbuuHeaderRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHighlight);
      if (containerRef.current) {
        containerRef.current.removeEventListener("scroll", scrollHandler);
      }
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <section className="flex w-full flex-col items-center font-urbanist">
      <h2 className="mb-20 text-center text-[32px] font-semibold text-black lg:text-[56px]">
        {t(`${translationPrefix}.comparison.title`)}
      </h2>

      <div
        ref={containerRef}
        className="mb-20 w-full max-w-[1280px] overflow-x-auto rounded-[32px] border border-[#F0F0F0]"
      >
        <div className="relative">
          {/**
           * This div is the single "highlight" behind the entire Bammbuu column.
           * We give it exactly the Figma CSS: background + border + border-radius.
           */}
          <div
            className="bammbuu-highlight"
            style={{
              top: 0,
              left: `${highlightLeft}px`,
              width: `${highlightWidth}px`,
              height: highlightHeight > 0 ? `${highlightHeight}px` : "auto",
            }}
          />

          <table
            ref={tableRef}
            className="z-1 relative w-full min-w-[900px] text-left"
          >
            <thead>
              <tr className="bg-[#F0F0F0] text-[18px] font-semibold text-[#141414]">
                {/* "Feature" header (rounded top-left corner) */}
                <th className="z-20 min-w-[304px] rounded-tl-3xl bg-[#F0F0F0] px-6 py-5">
                  {t(`${translationPrefix}.comparison.headers.feature`)}
                </th>

                {/**
                 * Bammbuu header:
                 *   • We removed border/background from this <th>.
                 *   • Its container <div.bammbuu-highlight> is providing background + border.
                 *   • We only keep z-20 so that text sits on top of that highlight.
                 *   • If you want the header text area to be fully opaque white (so no light‐green tint behind),
                 *     you can add bg-white. If you want it tinted, remove bg-white.
                 */}
                <th
                  ref={bammbuuHeaderRef}
                  className="relative z-20 min-w-[240px] px-6 py-5 text-[#042F0C]"
                >
                  {t(
                    `${translationPrefix}.comparison.headers.bammbuuLanguages`,
                  )}
                  <br />
                  <span className="text-lg font-semibold">
                    {(() => {
                      const subtitle = t(
                        `${translationPrefix}.comparison.headers.subtitle`,
                      );
                      return subtitle !==
                        `${translationPrefix}.comparison.headers.subtitle`
                        ? subtitle
                        : t(
                            `${translationPrefix}.comparison.headers.immersiveExamPrep`,
                          );
                    })()}
                  </span>
                </th>

                <th className="z-20 min-w-[180px] whitespace-nowrap bg-[#F0F0F0] px-6 py-5">
                  {t(`${translationPrefix}.comparison.headers.languageApps`)}
                </th>
                <th className="z-20 min-w-[180px] whitespace-nowrap bg-[#F0F0F0] px-6 py-5">
                  {t(
                    `${translationPrefix}.comparison.headers.liveTutorMarketplaces`,
                  )}
                </th>
                <th className="z-20 min-w-[180px] whitespace-nowrap rounded-tr-3xl bg-[#F0F0F0] px-6 py-5">
                  {t(
                    `${translationPrefix}.comparison.headers.genericOnlineCourses`,
                  )}
                </th>
              </tr>
            </thead>

            <tbody className="text-[16px]">
              {features.map((row, i) => (
                <tr
                  key={row.label}
                  className={`${i % 2 === 0 ? "bg-[#FCFCFC]" : "bg-white"} border border-[#F0F0F0]`}
                >
                  <td className="relative z-20 min-w-[304px] bg-transparent px-6 py-4 align-top font-semibold text-[#141414]">
                    {row.label}
                  </td>

                  {/**
                   * Bammbuu <td>:
                   * • No border, no background.  The "bammbuu-highlight" div does that.
                   * • z-20 so that icon/text sits on top of the highlight.
                   */}
                  <td className="relative z-20 min-w-[240px] py-4 pl-10 pr-6 text-[#042F0C]">
                    <span className="flex items-center gap-2">
                      {typeof row.bammbuu === "string" ? (
                        row.bammbuu
                      ) : (
                        <>
                          {row.bammbuu.icon && (
                            <img
                              src={iconMap[row.bammbuu.icon]}
                              alt="icon"
                              className="inline h-5 w-5"
                            />
                          )}
                          <span>{row.bammbuu.text}</span>
                        </>
                      )}
                    </span>
                  </td>

                  <td className="min-w-[180px] bg-transparent px-6 py-4">
                    {typeof row.apps === "string" ? (
                      row.apps
                    ) : (
                      <span className="flex items-center gap-2">
                        {row.apps.icon && (
                          <img
                            src={iconMap[row.apps.icon]}
                            alt="icon"
                            className="inline h-5 w-5"
                          />
                        )}
                        <span>{row.apps.text}</span>
                      </span>
                    )}
                  </td>

                  <td className="min-w-[180px] bg-transparent px-6 py-4">
                    {typeof row.tutors === "string" ? (
                      row.tutors
                    ) : (
                      <span className="flex items-center gap-2">
                        {row.tutors.icon && (
                          <img
                            src={iconMap[row.tutors.icon]}
                            alt="icon"
                            className="inline h-5 w-5"
                          />
                        )}
                        <span>{row.tutors.text}</span>
                      </span>
                    )}
                  </td>

                  <td className="min-w-[180px] bg-transparent px-6 py-4">
                    {typeof row.generic === "string" ? (
                      row.generic
                    ) : (
                      <span className="flex items-center gap-2">
                        {row.generic.icon && (
                          <img
                            src={iconMap[row.generic.icon]}
                            alt="icon"
                            className="inline h-5 w-5"
                          />
                        )}
                        <span>{row.generic.text}</span>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-[175px] flex flex-col gap-6 md:flex-row">
        <a
          href={
            translationPrefix === "fluencyChallenge"
              ? "https://calendly.com/bammbuu-languages/language-fluency-call-llamada-de-estrategia"
              : translationPrefix === "interviewAccelerator"
                ? "https://calendly.com/bammbuu-languages/career-strategy-english-audit"
                : "https://calendly.com/bammbuu-languages/info-call-llamada-de-informacion"
          }
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-black px-6 py-2 text-center text-base font-medium text-black transition hover:bg-gray-100"
        >
          {t(`${translationPrefix}.comparison.buttons.freeConsultation`)}
        </a>
        <button
          onClick={onEnrollClick}
          className="rounded-full border border-black bg-[#FFBF00] px-6 py-2 text-center text-base font-medium text-black transition hover:bg-[#ffd94d]"
        >
          {t(`${translationPrefix}.comparison.buttons.imInterested`)}
        </button>
      </div>
    </section>
  );
};

export default ComparisonTable;
